export interface StoredLessonProgress {
    // One entry per section-scoped field (e.g. "discovery.selected", "mcq.checked") — kept as a
    // flat map rather than one big snapshot object so each section can read/write its own slice
    // independently without needing to know about any other section's state shape.
    state: Record<string, unknown>;
    savedAt: number;
}

// Keeps a lesson's in-progress answers/reveals/step-position alive across a full unmount —
// e.g. clicking a word inside the lesson to Add/Edit it and returning via the browser back
// button, which remounts LessonPage/LessonPlayer from scratch (LessonPage re-fetches the lesson
// and only then mounts a brand-new LessonPlayer instance, so component state alone can't survive
// that trip). Mirrors PracticeService's per-id localStorage map + eviction-cap pattern.
//
// The store is parsed from localStorage once and then kept in memory; saves update the in-memory
// copy and are flushed to localStorage in one write per tick (and on page hide), rather than a
// full parse + stringify + setItem of every lesson's state for each field on every change — a
// player mount alone touches ~15 fields, and each keystroke in a text answer is another save.
// Completion itself (XP awarded or not) is NOT tracked here — that's server state, see
// Lesson.completed_at.
const STORAGE_KEY = 'lessonProgress';
const MAX_LESSONS = 20;

let cache: Record<string, StoredLessonProgress> | null = null;
let flushScheduled = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function store(): Record<string, StoredLessonProgress> {
    if (cache) return cache;
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        cache = raw ? JSON.parse(raw) : {};
    } catch {
        cache = {};
    }
    return cache as Record<string, StoredLessonProgress>;
}

function flush(): void {
    flushScheduled = false;
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = null;
    }
    if (!cache) return;
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch {
        // storage unavailable/full — progress just won't persist this time, not fatal
    }
}

function scheduleFlush(): void {
    if (flushScheduled) return;
    flushScheduled = true;
    flushTimer = setTimeout(flush, 0);
}

if (typeof window !== 'undefined') {
    // Don't lose a pending write when the tab is closed/backgrounded before the timer fires.
    window.addEventListener('pagehide', () => { if (flushScheduled) flush(); });
    // Another tab changed the store — drop the in-memory copy so the next read picks it up.
    window.addEventListener('storage', (e) => { if (e.key === STORAGE_KEY) cache = null; });
}

export const lessonProgressService = {
    loadField: <T,>(lessonId: number, field: string, fallback: T): T => {
        const value = store()[String(lessonId)]?.state?.[field];
        return value === undefined ? fallback : (value as T);
    },

    // Merges into whatever else is already saved for this lesson — each section persists its
    // own field independently, so this must never clobber another section's saved state.
    saveField: (lessonId: number, field: string, value: unknown): void => {
        const s = store();
        const key = String(lessonId);
        const entry = s[key] ?? { state: {}, savedAt: 0 };
        entry.state[field] = value;
        entry.savedAt = Date.now();
        s[key] = entry;

        const keys = Object.keys(s);
        if (keys.length > MAX_LESSONS) {
            keys
                .sort((a, b) => s[a].savedAt - s[b].savedAt)
                .slice(0, keys.length - MAX_LESSONS)
                .forEach((k) => delete s[k]);
        }
        scheduleFlush();
    },

    // Progress is keyed by lesson id alone, so an entry can outlive its lesson and be picked up by
    // an unrelated one that later gets the same id (the database was reset or restored from a
    // backup while this browser kept its localStorage) — the new lesson then opens with the old
    // one's answers already filled in. Anything saved before the lesson was created can't belong
    // to it, so drop it. Call with every lesson before its progress is read.
    discardStale: (lessons: { id: number; created_at: string }[]): void => {
        const s = store();
        let changed = false;
        for (const lesson of lessons) {
            const entry = s[String(lesson.id)];
            const createdAt = Date.parse(lesson.created_at);
            if (entry && !Number.isNaN(createdAt) && entry.savedAt < createdAt) {
                delete s[String(lesson.id)];
                changed = true;
            }
        }
        if (changed) flush();
    },

    // Called by LessonList's "Restart" — wipes the browser-side progress only. The server-side
    // completion (and the one-time XP award) is unaffected by design.
    clear: (lessonId: number): void => {
        delete store()[String(lessonId)];
        flush();
    },

    /** Test-only: forget the in-memory copy so the next read re-parses localStorage. */
    resetCacheForTests: (): void => {
        if (flushTimer) clearTimeout(flushTimer);
        flushTimer = null;
        cache = null;
        flushScheduled = false;
    },
};
